import { useGetMealTypePreferences } from '@hungryroot/exp-175-diet-driven-preferences';
import { useExperiment } from '@hungryroot/experiments';
import { useApiV1TagGroup as useTagGroup, tagGroups } from '@hungryroot/tags';
import { type Quiz } from '../defs/quiz';
import { getOccasionPreferenceDietaryRestrictionParamFromQuiz } from '../utils/getOccasionPreferenceDietaryRestrictionParamFromQuiz';

export function useQuizDataNormalizer(persistedQuiz: Quiz | undefined) {
  const dietDrivenPreferencesVariant = useExperiment(175);
  const customerDislikesVariant = useExperiment(185);

  const mealTypePreferenceOptions = useGetMealTypePreferences(
    persistedQuiz !== undefined
      ? getOccasionPreferenceDietaryRestrictionParamFromQuiz(persistedQuiz)
      : undefined,
    dietDrivenPreferencesVariant
  );

  const { data: polarizingTagGroupData } = useTagGroup({
    tagGroupId: tagGroups.polarizing2,
    expandDislikes:
      customerDislikesVariant !== undefined && customerDislikesVariant > 0,
  });

  return {
    // Remove meal type response data that doesn't align with the data type presented to users. Ex: user had tag based local storage data saved for meal type questions from before exp 175 was turned on. User is then grouped into exp 175 where meal type questions are no longer tag based. This function will clear the tag based data.
    // Remove polarizing tag group data that doesn't align with the data presented to users. Ex: user is grouped into exp 185 variant and has data for new polarizing tags stored in local storage, then exp 185 is called in favor of the control. This function will clear data from tags that are no longer presented to the user.
    normalizeData: (nextQuiz: Quiz) => {
      if (
        mealTypePreferenceOptions === undefined ||
        polarizingTagGroupData === undefined
      ) {
        return nextQuiz;
      }

      const {
        breakfastOptionsById,
        lunchOptionsById,
        dinnerOptionsById,
        snacksOptionsById,
        sweetsOptionsById,
      } = mealTypePreferenceOptions;

      const mealTypeOptionsById = {
        breakfast: breakfastOptionsById,
        lunch: lunchOptionsById,
        dinner: dinnerOptionsById,
        snacks: snacksOptionsById,
        sweets: sweetsOptionsById,
      };

      const normalizedQuiz = Object.keys(nextQuiz).reduce(
        (acc, key) => {
          switch (key) {
            case 'dislike':
              if (!Array.isArray(nextQuiz[key])) {
                acc[key] = nextQuiz[key] ?? null;
                return acc;
              }

              const validTagIds = polarizingTagGroupData.tags[0].map(
                (tag) => tag.id
              );

              acc[key] = nextQuiz[key].filter((item) =>
                validTagIds.includes(item)
              );
              return acc;
            case 'breakfast':
            case 'lunch':
            case 'dinner':
            case 'snacks':
            case 'sweets':
              if (!Array.isArray(nextQuiz[key])) {
                acc[key] = nextQuiz[key] ?? null;
                return acc;
              }

              const validDataById = mealTypeOptionsById[key];

              if (validDataById === undefined || validDataById === null) {
                acc[key] = nextQuiz[key];
                return acc;
              }

              acc[key] = nextQuiz[key].filter((item) =>
                validDataById.includes(item)
              );
              return acc;
            default:
              acc[key] = nextQuiz[key as keyof typeof nextQuiz];
              return acc;
          }
        },
        {} as Record<string, unknown>
      );

      return normalizedQuiz as Quiz;
    },
  };
}
